import { useLocation } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useViewerRole, type ViewerRole } from "@/hooks/use-viewer-role";

/**
 * Sign-in state cue: a 3px accent strip fixed to the left edge of the
 * viewport, colored by viewer role. Present but not shouty — legible in
 * a screenshot, ignorable in normal use.
 *
 *   admin  → warning (amber)
 *   coach  → primary (brand)
 *   parent → chart-1 (soft blue)
 *   guest  → nothing rendered
 *
 * Also hidden on marketing/landing routes and inside /admin/* where the
 * cue is redundant.
 */

const ROLE_TOKEN: Record<Exclude<ViewerRole, "guest">, string> = {
  admin: "hsl(var(--gold))",
  coach: "hsl(var(--primary))",
  parent: "hsl(var(--chart-1))",
};

const ROLE_LABEL: Record<ViewerRole, string> = {
  admin: "Platform admin",
  coach: "Signed in as coach",
  parent: "Signed in as parent",
  guest: "Not signed in",
};

const HIDDEN_PREFIXES = [
  "/admin",
  "/rsvp-info",
  "/rpe-info",
  "/feedback-info",
  "/fixtures-info",
  "/attendance-info",
  "/docs",
  "/terms",
];

function isHiddenRoute(pathname: string): boolean {
  if (pathname === "/") return true; // marketing landing
  // localized marketing roots: /es, /fr, /pt-br, /ja (and their info pages)
  if (/^\/(es|fr|pt-br|ja)(\/|$)/i.test(pathname)) return true;
  return HIDDEN_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

export function ViewerAccentBar() {
  const { pathname } = useLocation();
  const { role, displayName, loading } = useViewerRole();

  if (loading) return null;
  if (role === "guest") return null;
  if (isHiddenRoute(pathname)) return null;

  const color = ROLE_TOKEN[role];
  const label = displayName
    ? `${ROLE_LABEL[role]} · ${displayName}`
    : ROLE_LABEL[role];

  return (
    <>
      <span className="sr-only" aria-live="polite">{label}</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            data-viewer-role={role}
            aria-hidden="true"
            className="fixed start-0 top-0 z-40 h-full w-[3px] pointer-events-auto"
            style={{
              backgroundColor: color,
              opacity: 0.55,
              paddingTop: "env(safe-area-inset-top)",
              paddingBottom: "env(safe-area-inset-bottom)",
            }}
          />
        </TooltipTrigger>
        <TooltipContent side="right" className="text-xs">
          {label}
        </TooltipContent>
      </Tooltip>
    </>
  );
}
